import { useEffect, useReducer, useRef, useState, type JSX, type RefObject } from 'react'
import { api, type Vendor } from '../lib/api'
import { Icon } from './ui'
import '../styles/lists.css'

// Choosing one vendor by typing part of its name or code.
//
// The Reports screen used to ask for a vendor's identifier in a text box
// (observation 3.17 of 17 September 2026), which nobody outside the database
// knows. This is the same choice made the way a person makes it: type a few
// letters, pick the name. The value it hands back is still the identifier,
// because that is what the report filters on.
//
// # THE CHOICE THAT IS NO VENDOR
//
// The Evidence screen's scope was a native select with "Every vendor in this
// tenant" above one option per vendor, which is a list nobody can use at three
// hundred vendors (the founder's review of 11 October 2026). A caller that
// passes `everyone` gets that choice here: it is what the box says while no
// vendor is chosen, and the first thing the list offers.

const SHOWN = 8

function nameOf(v: Vendor): string {
  return v.displayName || v.legalName
}

// One row of the list. An empty id is the choice that is no vendor.
export type VendorChoice = { id: string; name: string; code: string }

// Everything that answers to what has been typed: vendors whose name or code
// holds it. A vendor trading under another name is found by either. The list
// draws the first few of these and says how many it left out.
//
// The every-vendor choice is offered only while nothing is typed. It used to be
// offered whenever the letters typed were somewhere in its own words, first in
// the list and so the one Enter picked: "ant", typed for a vendor, is in
// "tenant", and Enter chose the whole tenant (first review, 11 October 2026).
// Somebody who has typed is looking for a vendor.
export function vendorChoices(vendors: Vendor[], typed: string, everyone?: string): VendorChoice[] {
  const q = typed.trim().toLowerCase()
  if (q === '') return everyone ? [{ id: '', name: everyone, code: '' }] : []
  const out: VendorChoice[] = []
  for (const v of vendors) {
    if ([v.displayName, v.legalName, v.vendorCode].some((s) => (s ?? '').toLowerCase().includes(q))) {
      out.push({ id: v.id, name: nameOf(v), code: v.vendorCode })
    }
  }
  return out
}

// The line under a list that is not the whole of what matched. "private" finds
// two hundred vendors in a register of three hundred, and eight rows with
// nothing under them would read as eight vendors.
export function heldBackNote(listed: number, matched: number): string {
  return matched > listed ? 'The first ' + listed + ' of ' + matched + ' are listed. Type more to narrow them.' : ''
}

// What the box shows. Its value is only ever what was typed, so the next
// letter typed is the start of a search and never lands on the end of a
// label. What an empty value means is said in the placeholder's place while
// the list is closed, and how to search while it is open.
export function pickerBox(typed: string, open: boolean, everyone: string | undefined, placeholder: string): { value: string; placeholder: string } {
  return { value: typed, placeholder: everyone && !open ? everyone : placeholder }
}

// What the box holds, whether its list is open and which row is lit, and what
// each thing a person does to it makes of that. Plain code, so the sequences
// two reviews found wrong (11 October 2026) can be played in a test: a letter
// typed after a pick or after Escape begins a new search, and a click or the
// Down key on a box whose list was closed opens it again.
export type PickerState = { text: string; open: boolean; active: number }

export type PickerEvent =
  | { type: 'focus' }
  | { type: 'click' }
  | { type: 'typed'; text: string }
  // rows is how many the list holds, which is as far down as Down goes.
  | { type: 'down'; rows: number }
  | { type: 'up' }
  | { type: 'escape' }
  | { type: 'picked' }
  // The focus, or a press of the mouse, went somewhere else.
  | { type: 'left' }

export const PICKER_AT_REST: PickerState = { text: '', open: false, active: 0 }

export function pickerStep(s: PickerState, e: PickerEvent): PickerState {
  switch (e.type) {
    case 'focus':
    case 'click':
      return s.open ? s : { ...s, open: true }
    case 'typed':
      return { text: e.text, open: true, active: 0 }
    case 'down':
      // On a closed box the first press opens the list where it was.
      return s.open ? { ...s, active: Math.max(0, Math.min(s.active + 1, e.rows - 1)) } : { ...s, open: true }
    case 'up':
      return { ...s, active: Math.max(s.active - 1, 0) }
    case 'escape':
    case 'picked':
      return PICKER_AT_REST
    case 'left':
      // The letters stay. They are not a choice, and a screen that acts on
      // the choice has to be able to see that there are some (onTyped).
      return s.open ? { ...s, open: false } : s
  }
}

// What the box's handlers send, and who else is told. The letters in the box
// are reported whenever they change: they are not a choice, and a screen that
// is about to act on the choice has to know there are some. With this call
// left out, a name typed and tabbed away from was built as every vendor.
//
// Escape and a pick are reported whether or not the box held anything. They
// are the person saying "no letters", and a screen that still remembers some
// from before this picker was drawn has to hear it.
export function pickerSend(
  state: PickerState,
  e: PickerEvent,
  dispatch: (e: PickerEvent) => void,
  onTyped?: (text: string) => void,
): void {
  const next = pickerStep(state, e)
  if (next.text !== state.text || e.type === 'escape' || e.type === 'picked') onTyped?.(next.text)
  dispatch(e)
}

type PickerProps = {
  id: string
  label: string
  value: string
  onChange: (vendorId: string) => void
  placeholder?: string
  // In a row of controls the label is read by a screen reader and not drawn,
  // so the box lines up with the buttons beside it.
  compact?: boolean
  // What an empty value means, in words, where it means something: "Every
  // vendor in this tenant". Absent, an empty value is nothing chosen yet.
  everyone?: string
  // Words that stand in the picker's place while the choice may not be
  // changed (a pack is being built for it). Nothing here can be pressed then.
  locked?: string
}

// The list could not be read ('failed'), or is being read again after a read
// that failed ('reading').
export type Unread = 'failed' | 'reading' | null

export function VendorPicker({
  options,
  onTyped,
  ...props
}: PickerProps & {
  // The vendor list, when the screen already holds it. A screen with one
  // picker per row (the documents of an import) reads the list once and hands
  // it to every picker, rather than each picker reading it again.
  options?: Vendor[] | null
  // Told whenever the letters in the box change, and with nothing when one is
  // chosen or they are cleared. See pickerSend.
  onTyped?: (text: string) => void
  // For the screen that hands the list in, which is the one that knows its
  // read failed and can read it again. A picker that reads its own list
  // knows both for itself.
  unread?: Unread
  onRetry?: () => void
}): JSX.Element {
  const [loaded, setLoaded] = useState<Vendor[] | null>(null)
  const [ownUnread, setOwnUnread] = useState<Unread>(null)
  const [attempt, setAttempt] = useState(0)
  const given = options !== undefined
  const vendors = given ? options : loaded
  const unread = given ? (props.unread ?? null) : ownUnread
  const retry = given
    ? props.onRetry
    : () => {
        setOwnUnread('reading')
        setAttempt((n) => n + 1)
      }
  const [box, dispatch] = useReducer(pickerStep, PICKER_AT_REST)
  const send = (e: PickerEvent) => pickerSend(box, e, dispatch, onTyped)
  const wrap = useRef<HTMLDivElement>(null)
  const input = useRef<HTMLInputElement>(null)
  // Clearing a chosen vendor takes its button off the page, and the focus
  // with it. The reader cleared it to choose again, so the box takes it.
  const refocus = useRef(false)

  useEffect(() => {
    if (given) return
    let live = true
    api
      .vendors({})
      .then((p) => {
        if (!live) return
        setLoaded(p.vendors)
        setOwnUnread(null)
      })
      .catch(() => {
        if (live) setOwnUnread('failed')
      })
    return () => {
      live = false
    }
  }, [given, attempt])

  // The box goes when the list cannot be read (see VendorPickerView), and the
  // letters in it go with it. Kept, they would come back with the box after a
  // read that worked, and a screen told of them would go on holding its
  // button over a box that is not there: the fault of the final re-review
  // (11 October 2026), by another road.
  const boxGone = unread !== null
  const retried = useRef(false)
  const again = useRef<HTMLButtonElement>(null)
  // Whether the box is going from under somebody who is typing in it. Looked
  // at while drawing: by the time an effect runs the box has left the page,
  // and the focus has gone to nothing with it.
  const typingAsItGoes = useRef(false)
  if (boxGone && input.current !== null && document.activeElement === input.current) typingAsItGoes.current = true
  useEffect(() => {
    if (boxGone) {
      send({ type: 'escape' })
      if (typingAsItGoes.current) again.current?.focus()
      typingAsItGoes.current = false
      return
    }
    // Read again and read: the button that was pressed has gone, and the
    // focus with it, unless the person has taken it elsewhere meanwhile.
    const at = document.activeElement
    if (retried.current && (!at || at === document.body)) {
      const first = input.current ?? wrap.current?.querySelector('button')
      first?.focus()
    }
    retried.current = false
    // send is made on every draw, and is the same thing each time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boxGone])

  const open = box.open
  useEffect(() => {
    if (!open) return
    const away = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) dispatch({ type: 'left' })
    }
    document.addEventListener('mousedown', away)
    return () => document.removeEventListener('mousedown', away)
  }, [open])

  const chosen = vendors?.some((v) => v.id === props.value) ?? false
  useEffect(() => {
    if (!chosen && refocus.current) input.current?.focus()
    refocus.current = false
  }, [chosen])

  return (
    <VendorPickerView
      {...props}
      vendors={vendors}
      unread={unread}
      onRetry={
        retry &&
        (() => {
          retried.current = true
          retry()
        })
      }
      box={box}
      send={send}
      wrap={wrap}
      input={input}
      again={again}
      onClear={() => {
        refocus.current = true
        props.onChange('')
      }}
    />
  )
}

// The picker as it is drawn, from the state of its box. No state of its own,
// so a test can draw it in any state and press its keys: what Enter does over
// a closed list is decided here, and that is where the test looks.
export function VendorPickerView({
  id,
  label,
  value,
  onChange,
  placeholder = 'Search by name or code',
  compact = false,
  everyone,
  locked,
  vendors,
  unread,
  onRetry,
  box: { text, open, active },
  send,
  wrap,
  input,
  again,
  onClear,
}: PickerProps & {
  vendors: Vendor[] | null
  unread: Unread
  onRetry?: () => void
  box: PickerState
  send: (e: PickerEvent) => void
  wrap?: RefObject<HTMLDivElement | null>
  input?: RefObject<HTMLInputElement | null>
  again?: RefObject<HTMLButtonElement | null>
  onClear: () => void
}): JSX.Element {
  const chosen = vendors?.find((v) => v.id === value) ?? null
  const matched = vendorChoices(vendors ?? [], text, everyone)
  const matches = matched.slice(0, SHOWN)
  const heldBack = heldBackNote(matches.length, matched.length)
  const listed = open && matches.length > 0
  const shown = pickerBox(text, open, everyone, placeholder)

  function pick(c: VendorChoice) {
    onChange(c.id)
    send({ type: 'picked' })
  }

  const frame = 'field vpick' + (compact ? ' vpick-compact' : '') + (everyone ? ' vpick-default' : '')
  if (locked) {
    return (
      <div className={frame}>
        <label className={compact ? 'vh' : undefined} id={id + '-label'}>
          {label}
        </label>
        {/* Not a disabled box: a disabled box says nothing to a screen reader
            that is not on it, and this has something to say. */}
        <span className="vpick-locked" id={id} role="status" aria-labelledby={id + '-label'}>
          {locked}
        </span>
      </div>
    )
  }

  // A vendor already chosen is still named: its name was read before.
  if (unread && !chosen) {
    const reading = unread === 'reading'
    return (
      <div className={frame} ref={wrap}>
        <label className={compact ? 'vh' : undefined} id={id + '-label'}>
          {label}
        </label>
        {/* In the box's place, and not a disabled box with the reason for a
            placeholder: that was grey on grey, read out by nothing, and no
            way on. One button, as the audit bar has for acts it could not
            read, which keeps the focus while the list is read again. */}
        <span className="vpick-unread" role="alert">
          <button
            type="button"
            id={id}
            ref={again}
            aria-labelledby={id + '-label ' + id}
            aria-disabled={reading}
            onClick={() => {
              if (!reading) onRetry?.()
            }}
          >
            {!reading && <Icon name="alert" size={14} />}
            {reading ? 'Reading the vendor list' : 'The vendor list could not be read. Try again.'}
          </button>
        </span>
      </div>
    )
  }

  return (
    <div
      className={frame + (open ? ' vpick-open' : '')}
      ref={wrap}
      onBlur={(e) => {
        // The list does not stay open behind a reader who has tabbed on.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) send({ type: 'left' })
      }}
    >
      <label htmlFor={id} className={compact ? 'vh' : undefined}>
        {label}
      </label>
      {chosen ? (
        <span className="vpick-chosen">
          <span>{nameOf(chosen)}</span>
          <span className="mono muted">{chosen.vendorCode}</span>
          <button type="button" className="vpick-clear" onClick={onClear} aria-label={'Clear ' + nameOf(chosen)}>
            ×
          </button>
        </span>
      ) : (
        <>
          <input
            id={id}
            ref={input}
            type="search"
            role="combobox"
            aria-expanded={listed}
            aria-controls={id + '-list'}
            aria-activedescendant={listed ? id + '-opt-' + active : undefined}
            aria-autocomplete="list"
            autoComplete="off"
            value={shown.value}
            placeholder={shown.placeholder}
            onChange={(e) => send({ type: 'typed', text: e.target.value })}
            onFocus={() => send({ type: 'focus' })}
            // A box that already has the focus gets no focus event from a
            // click, so a list closed with Escape stayed closed under it.
            onClick={() => send({ type: 'click' })}
            onKeyDown={(e) => {
              // Enter picks the lit row of a list that is on the screen, and
              // nothing from one that is not.
              const here = listed ? matches[active] : undefined
              if (e.key === 'ArrowDown') {
                e.preventDefault()
                send({ type: 'down', rows: matches.length })
              } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                send({ type: 'up' })
              } else if (e.key === 'Enter' && here) {
                e.preventDefault()
                pick(here)
              } else if (e.key === 'Escape') {
                // A search box clears itself on Escape, and that clearing is
                // an edit, which opened the list again the moment it closed.
                // The clearing is done here instead, once. With a list on the
                // screen the press is spent on it, so a drawer holding the
                // picker does not close with it.
                e.preventDefault()
                if (listed) e.stopPropagation()
                send({ type: 'escape' })
              }
            }}
          />
          {everyone && (
            <span className="vpick-caret" aria-hidden="true">
              <Icon name="chevron" size={14} />
            </span>
          )}
          {listed && (
            <ul className="vpick-list" id={id + '-list'} role="listbox">
              {matches.map((c, i) => (
                <li
                  key={c.id || 'everyone'}
                  id={id + '-opt-' + i}
                  role="option"
                  aria-selected={i === active}
                  className={i === active ? 'on' : undefined}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    pick(c)
                  }}
                >
                  <span>{c.name}</span>
                  <span className="mono muted">{c.code}</span>
                </li>
              ))}
              {everyone && text.trim() === '' && (
                <li className="vpick-hint muted" role="presentation" onMouseDown={(e) => e.preventDefault()}>
                  Type a name or a code to choose one vendor
                </li>
              )}
              {heldBack && (
                <li className="vpick-hint muted" role="presentation" onMouseDown={(e) => e.preventDefault()}>
                  {heldBack}
                </li>
              )}
            </ul>
          )}
          {open && text.trim() !== '' && vendors && matches.length === 0 && (
            <p className="vpick-none muted">No vendor matches</p>
          )}
        </>
      )}
    </div>
  )
}
